/**
 * Form Management Service — uses the existing backend APIs
 *
 * All operations map to the same endpoints already used by the
 * old ConsentForm / CosentForm.jsx so no new backend work is needed.
 *
 * Existing API map:
 *   List    → GET  /vendors/getAllForms
 *   Create  → POST /vendors/addConsentQuestion
 *   Update  → PUT  /vendors/updateConsentform/:formId
 *   Delete  → DEL  /vendors/deleteConsentForm/:formId
 *   Toggle  → PUT  /vendors/updateConsentFormStatus/:formId
 *
 * The "sections + fields" model is flattened to the old "questions" array
 * when sending to the API, and expanded back when reading.
 */

import { httpService } from "./httpService";
import {
  getSectionServices,
  getBoardingFormTemplate,
  getOnboardingFormTemplate,
  isCommonBasicSection,
  NEUTERED_STATUS_OPTIONS,
} from "../components/FormManagement/boardingFormTemplate";

// ─── Template Lookups ────────────────────────────────────────────────────────

const buildTemplateLookups = () => {
  const mapsToLookup = {};
  const labelLookup = {};
  const sectionServicesLookup = {};

  const indexTemplate = (template) => {
    if (!template || !Array.isArray(template.sections)) return;
    template.sections.forEach((sec) => {
      const sName = sec.sectionName;
      if (!sName) return;
      const srvList = getSectionServices(sec);
      if (srvList.length > 0) {
        sectionServicesLookup[sName.toLowerCase()] = srvList;
      }
      (sec.fields || []).forEach((f) => {
        const mapsTo = f.mapsTo || f.maps_to;
        if (mapsTo && mapsTo !== "custom" && mapsTo !== "section") {
          mapsToLookup[mapsTo] = sName;
        }
        if (f.label) {
          labelLookup[String(f.label).trim().toLowerCase()] = sName;
        }
      });
    });
  };

  try {
    indexTemplate(getBoardingFormTemplate());
    indexTemplate(getOnboardingFormTemplate());
  } catch (e) {
    console.error("Failed to build template lookups:", e);
  }

  return { mapsToLookup, labelLookup, sectionServicesLookup };
};

// ─── Payload converters ──────────────────────────────────────────────────────

/**
 * Convert the new {sections[]} model into the old {questions[]} the backend
 * expects. Each field in a section becomes one question.
 *
 * FIX: every question (heading + fields) gets a global running `order`,
 * and headings send their `_id` (section.mongoId) so the backend updates
 * the same row instead of inserting a new heading at the end.
 */
const sectionsToQuestions = (sections = [], formServices = []) => {
  const questions = [];
  let order = 0; // NEW: global running order across headings + fields

  sections.forEach((section, sIdx) => {
    const isCommon = isCommonBasicSection(section);
    const srvList = isCommon ? [] : getSectionServices(section, formServices);
    const secName = section.sectionName || `Section ${sIdx + 1}`;
    const secId = section.id;
    const secMongoId =
      section.mongoId &&
      typeof section.mongoId === "string" &&
      /^[0-9a-fA-F]{24}$/.test(section.mongoId)
        ? section.mongoId
        : null;

    // Add a "section_heading" sentinel question so we know where each section starts
    questions.push({
      _id: secMongoId, // NEW: keep the same heading row on update
      order: ++order, // NEW
      question: secName,
      answerType: "section_heading",
      isRequired: false,
      enabled: section.enabled !== false,
      options: [],
      allowMultiple: false,
      mapsTo: "section",
      maps_to: "section",
      fieldUid: secId,
      field_uid: secId,
      section_id: secMongoId,
      section_key: secId,
      sectionName: secName,
      section_name: secName,
      section: secName,
      description: section.sectionDescription || "",
      associated_services: srvList,
      associatedServices: srvList,
      associatedService: srvList.join(", "),
      sectionMeta: JSON.stringify({
        sectionName: secName,
        sectionDescription: section.sectionDescription || "",
        displayOrder: section.displayOrder || sIdx + 1,
        sectionId: secId,
        repeatable: section.repeatable || false,
        associated_services: srvList,
        associatedServices: srvList,
        associatedService: srvList.join(", "),
      }),
    });

    (section.fields || []).forEach((field) => {
      const mapsTo = field.mapsTo || field.maps_to || "custom";
      const fieldUid = field.fieldUid || field.field_uid || field.id;
      questions.push({
        _id: field.mongoId || field._id,
        order: ++order, // NEW
        question: field.label || "",
        answerType: field.fieldType || "short_text",
        isRequired: field.required || false,
        enabled: field.enabled !== false,
        allowMultiple:
          field.fieldType === "multiple_choice" || field.allowMultiple || false,
        mapsTo,
        maps_to: mapsTo,
        fieldUid,
        field_uid: fieldUid,
        section_id: secMongoId,
        section_key: secId,
        sectionName: secName,
        section_name: secName,
        section: secName,
        description: field.helpText || "",
        associated_services: srvList,
        associatedServices: srvList,
        associatedService: srvList.join(", "),
        options: (field.options || []).map((opt, i) => ({
          optionText: opt.label || opt.value || opt,
          order: i + 1,
        })),
        fieldMeta: JSON.stringify({
          fieldId: field.id,
          fieldUid,
          mapsTo,
          fieldType: field.fieldType,
          placeholder: field.placeholder || "",
          helpText: field.helpText || "",
          defaultValue: field.defaultValue || "",
          validation: field.validation || {},
          searchable: field.searchable || false,
          locked: field.locked === true,
          disableAllowed: field.disableAllowed !== false,
          sectionId: secId,
          sectionName: secName,
          associated_services: srvList,
          associatedServices: srvList,
          associatedService: srvList.join(", "),
        }),
      });
    });
  });

  return questions;
};

/**
 * Convert the old {questions[]} array back to the new {sections[]} model.
 * Reads sentinel "section_heading" questions as well as field meta, section_name,
 * and template mapsTo/label lookups to properly distribute fields into sections.
 *
 * FIX: the heading's DB _id is stored on the section as `mongoId`.
 */
const questionsToSections = (questions = []) => {
  if (!Array.isArray(questions) || questions.length === 0) {
    return [];
  }

  const sections = [];
  const { mapsToLookup, labelLookup, sectionServicesLookup } = buildTemplateLookups();
  let currentSection = null;

  const findOrCreateSection = (name, id = null, srv = null, desc = "", displayOrder = null) => {
    const cleanName = (name || "").trim() || "General";

    // Look up by sectionId first if present, otherwise by case-insensitive sectionName
    let section = sections.find(
      (s) =>
        (id && s.id === id) ||
        s.sectionName.trim().toLowerCase() === cleanName.toLowerCase()
    );

    if (!section) {
      const defaultSrv = sectionServicesLookup[cleanName.toLowerCase()] || [];
      const servicesToUse = Array.isArray(srv) && srv.length > 0 ? srv : defaultSrv;
      section = {
        id: id || `sec-${Date.now()}-${sections.length + 1}`,
        sectionName: cleanName,
        sectionDescription: desc || "",
        displayOrder: displayOrder || sections.length + 1,
        repeatable: false,
        associated_services: servicesToUse,
        associatedServices: servicesToUse,
        associatedService: servicesToUse.join(", "),
        collapsed: false,
        fields: [],
      };
      sections.push(section);
    } else {
      if (id && !section.id) section.id = id;
      if (desc && !section.sectionDescription) section.sectionDescription = desc;
      if (Array.isArray(srv) && srv.length > 0) {
        section.associated_services = srv;
        section.associatedServices = srv;
        section.associatedService = srv.join(", ");
      }
    }
    return section;
  };

  questions.forEach((q, idx) => {
    const answerType = String(q.answerType || "").toLowerCase();
    const mapsTo = q.maps_to || q.mapsTo || "custom";
    const isHeading =
      answerType === "section_heading" ||
      answerType === "section_header" ||
      mapsTo === "section";

    if (isHeading) {
      let meta = {};
      try { meta = JSON.parse(q.sectionMeta || "{}"); } catch { /* ignore */ }

      const rawSrv =
        q.associated_services ||
        q.associatedServices ||
        meta.associated_services ||
        meta.associatedServices ||
        meta.associatedService ||
        [];
      const sectionServices = Array.isArray(rawSrv)
        ? rawSrv.map((s) => String(s).trim()).filter(Boolean)
        : typeof rawSrv === "string" && rawSrv.trim()
          ? rawSrv.split(",").map((s) => s.trim()).filter(Boolean)
          : [];

      const secName = meta.sectionName || q.sectionName || q.section_name || q.question || `Section ${sections.length + 1}`;
      const secId = meta.sectionId || q.section_key || q.field_uid || q.fieldUid || `sec-heading-${idx}`;
      const secDesc = meta.sectionDescription || q.description || "";

      currentSection = findOrCreateSection(
        secName,
        secId,
        sectionServices,
        secDesc,
        meta.displayOrder || idx + 1
      );

      if (currentSection) {
        // Preserve the section-level enable flag that was stored on the heading
        currentSection.enabled = q.enabled !== false;
        currentSection.mongoId = q._id; // NEW: remember the heading's DB id
      }
      return;
    }

    // It's a field question
    let fieldMeta = {};
    try { fieldMeta = JSON.parse(q.fieldMeta || "{}"); } catch { /* ignore */ }

    const rawLabel = String(q.question || "").trim();
    const cleanLabel = rawLabel.toLowerCase();

    // Determine target section name for this field
    const isExplicitSection = (name) => {
      if (!name) return false;
      const s = String(name).trim().toLowerCase();
      return s && s !== "other" && s !== "general";
    };

    let targetSectionName = null;
    let targetSectionId = fieldMeta.sectionId || q.section_key || null;

    if (isExplicitSection(fieldMeta.sectionName)) {
      targetSectionName = fieldMeta.sectionName;
    } else if (isExplicitSection(q.sectionName)) {
      targetSectionName = q.sectionName;
    } else if (isExplicitSection(q.section_name)) {
      targetSectionName = q.section_name;
    } else if (currentSection) {
      // Sentinel heading was encountered right above this field: keep it in current section!
      targetSectionName = currentSection.sectionName;
      if (!targetSectionId) targetSectionId = currentSection.id;
    } else if (isExplicitSection(q.section)) {
      targetSectionName = q.section;
    } else if (mapsToLookup[mapsTo]) {
      targetSectionName = mapsToLookup[mapsTo];
    } else if (labelLookup[cleanLabel]) {
      targetSectionName = labelLookup[cleanLabel];
    } else {
      targetSectionName = (q.section && q.section.toLowerCase() !== "other") ? q.section : "General";
    }

    let sec;
    if (
      currentSection &&
      currentSection.sectionName.trim().toLowerCase() === String(targetSectionName || "").trim().toLowerCase()
    ) {
      sec = currentSection;
    } else {
      sec = findOrCreateSection(targetSectionName, targetSectionId);
      currentSection = sec;
    }

    const fieldUid = q.field_uid || q.fieldUid || fieldMeta.fieldUid || fieldMeta.fieldId || q._id || `fld-${idx}-${Date.now()}`;

    let fieldOptions = (q.options || []).map((opt) => ({
      label: opt.optionText || opt.label || opt,
      value: opt.optionText || opt.value || opt,
    }));

    const isNeuteredField =
      mapsTo === "pet.neutered_status" ||
      cleanLabel === "neutered / spayed status";

    if (isNeuteredField) {
      const hasLegacyYesNo = fieldOptions.some((o) => {
        const val = String(o.label || o.value || "").trim().toLowerCase();
        return val === "yes" || val === "no";
      });
      if (fieldOptions.length === 0 || hasLegacyYesNo) {
        fieldOptions = (NEUTERED_STATUS_OPTIONS || [
          "Not Neutered",
          "Neutered",
          "Unknown",
        ]).map((s) => ({ label: s, value: s }));
      }
    }

    sec.fields.push({
      id: fieldUid,
      fieldUid: fieldUid,
      mongoId: q._id,
      label: q.question || "",
      fieldType: fieldMeta.fieldType || q.answerType || "short_text",
      required: q.isRequired || false,
      enabled: q.enabled !== false,
      mapsTo,
      placeholder:
        fieldMeta.placeholder ||
        (isNeuteredField ? "Select Neutered Status" : ""),
      helpText: fieldMeta.helpText || "",
      defaultValue: fieldMeta.defaultValue || q.defaultValue || "",
      validation: fieldMeta.validation || {},
      searchable: fieldMeta.searchable || false,
      locked: fieldMeta.locked === true,
      disableAllowed: fieldMeta.disableAllowed !== false,
      allowMultiple: q.allowMultiple || false,
      options: fieldOptions,
    });
  });

  // Clean up: if an empty "General" section was created but other sections exist with fields, remove "General"
  const populatedSections = sections.filter(
    (s) => !(s.sectionName.trim().toLowerCase() === "general" && s.fields.length === 0 && sections.length > 1)
  );

  return populatedSections.length > 0 ? populatedSections : sections;
};

/**
 * Extract the form ID from whatever shape the API returns.
 */
const extractId = (form) => {
  if (!form) return null;
  return (
    form._id || form.form_id || form.formId || form.formID ||
    form.formid || form.id ||
    form.raw?._id || form.raw?.form_id || form.raw?.formId ||
    form.raw?.id || null
  );
};

/**
 * Safely extract an array from various API response shapes.
 */
const extractArray = (res) => {
  if (Array.isArray(res?.data?.data)) return res.data.data;
  if (Array.isArray(res?.data)) return res.data;
  if (Array.isArray(res?.forms)) return res.forms;
  if (Array.isArray(res?.data?.forms)) return res.data.forms;
  if (Array.isArray(res)) return res;
  return [];
};

// ─── Public API ──────────────────────────────────────────────────────────────

export const formManagementService = {
  /**
   * List all forms.
   * Uses: GET /vendors/getAllForms  (same as CosentForm.jsx)
   */
  getAllForms: async () => {
    const res = await httpService.get("/vendors/getAllForms");
    const raw = extractArray(res);
    // Enrich each form with restored sections so the list can show counts
    return raw.map((form) => {
      const formId = extractId(form);
      const questions = Array.isArray(form.questions) ? form.questions : [];
      const sections = questionsToSections(questions);
      return {
        ...form,
        _id: formId,
        id: formId,
        form_id: formId,
        formId: formId,
        backendId: formId,
        // Expose sections on each form object
        sections,
        // Normalise name field
        formName: form.formName || form.form_name || form.name || "",
        // Map "enabled" boolean to "status" string for the list UI
        status: form.enabled === false ? "inactive"
               : (form.status || "active"),
        // Category derived from services array or formCategory field
        formCategory: form.formCategory || form.category ||
                      (form.services && form.services[0]) || "general",
        formType: form.formType || "custom",
        formFrequency: form.formFrequency || "every_time",
        validityPeriod: form.validityPeriod || 12,
        requirement: form.requirement || "optional",
        services: Array.isArray(form.services) ? form.services : (form.formCategory ? [form.formCategory] : []),
      };
    });
  },

  /**
   * Get a single form (with full sections+fields).
   * Uses: GET /vendors/getAllForms then filter by id (no single-form endpoint exists yet)
   */
  getFormById: async (formId) => {
    try {
      const res = await httpService.get("/vendors/getAllForms");
      const all = extractArray(res);
      const found = all.find((f) => String(extractId(f)) === String(formId));
      if (!found) return null;
      const actualId = extractId(found) || formId;
      const questions = Array.isArray(found.questions) ? found.questions : [];
      return {
        ...found,
        _id: actualId,
        id: actualId,
        form_id: actualId,
        formId: actualId,
        backendId: actualId,
        sections: questionsToSections(questions),
        formName: found.formName || found.form_name || found.name || "",
        status: found.enabled === false ? "inactive" : (found.status || "active"),
        formCategory: found.formCategory || found.category ||
                      (found.services && found.services[0]) || "general",
        formType: found.formType || "custom",
        formFrequency: found.formFrequency || "every_time",
        validityPeriod: found.validityPeriod || 12,
        requirement: found.requirement || "optional",
        services: Array.isArray(found.services) ? found.services : (found.formCategory ? [found.formCategory] : []),
      };
    } catch {
      return null;
    }
  },

  /**
   * Create a new form.
   * Uses: POST /vendors/addConsentQuestion  (same as CosentForm.jsx)
   */
  createForm: async ({
    formName,
    formDescription,
    formCategory,
    formType,
    status,
    formFrequency = "every_time",
    validityPeriod = 12,
    requirement = "optional",
    services = [],
    sections = [],
  }) => {
    const targetServices = Array.isArray(services) && services.length > 0 ? services : (formCategory ? [formCategory] : []);
    const payload = {
      formName: formName || "Untitled Form",
      services: targetServices,
      requirement: requirement || "optional",
      formFrequency: formFrequency || "every_time",
      validityPeriod: validityPeriod || 12,
      formDescription: formDescription || "",
      formType: formType || "custom",
      enabled: status !== "inactive",
      questions: sectionsToQuestions(sections, targetServices),
    };
    const res = await httpService.post("/vendors/addConsentQuestion", payload);
    return res?.data ?? res;
  },

  /**
   * Update an existing form.
   * Uses: PUT /vendors/updateConsentform/:formId  (same as CosentForm.jsx)
   */
  updateForm: async (
    formId,
    {
      formName,
      formDescription,
      formCategory,
      formType,
      status,
      formFrequency = "every_time",
      validityPeriod = 12,
      requirement = "optional",
      services = [],
      sections = [],
    }
  ) => {
    const targetServices = Array.isArray(services) && services.length > 0 ? services : (formCategory ? [formCategory] : []);
    const payload = {
      formName: formName || "Untitled Form",
      services: targetServices,
      requirement: requirement || "optional",
      formFrequency: formFrequency || "every_time",
      validityPeriod: validityPeriod || 12,
      formDescription: formDescription || "",
      formType: formType || "custom",
      enabled: status !== "inactive",
      questions: sectionsToQuestions(sections, targetServices),
    };
    const res = await httpService.put(`/vendors/updateConsentform/${formId}`, payload);
    return res?.data ?? res;
  },

  /**
   * Delete a form.
   * Uses: DELETE /vendors/deleteConsentForm/:formId  (same as CosentForm.jsx)
   */
  deleteForm: async (formId) => {
    const res = await httpService.delete(`/vendors/deleteConsentForm/${formId}`);
    return res;
  },

  /**
   * Activate a form (publish).
   * Uses: PUT /vendors/updateConsentFormStatus/:formId  (same as CosentForm.jsx)
   */
  publishForm: async (formId) => {
    const res = await httpService.put(`/vendors/updateConsentFormStatus/${formId}`, { status: true });
    return res;
  },

  /**
   * Deactivate a form.
   * Uses: PUT /vendors/updateConsentFormStatus/:formId  (same as CosentForm.jsx)
   */
  deactivateForm: async (formId) => {
    const res = await httpService.put(`/vendors/updateConsentFormStatus/${formId}`, { status: false });
    return res;
  },

  /**
   * Duplicate a form — create a copy using addConsentQuestion.
   * There is no dedicated duplicate endpoint; we fetch the form and re-create it.
   */
  duplicateForm: async (formId) => {
    try {
      const original = await formManagementService.getFormById(formId);
      if (!original) throw new Error("Form not found");
      return formManagementService.createForm({
        ...original,
        formName: `${original.formName} (Copy)`,
        status: "draft",
      });
    } catch (err) {
      console.error("Duplicate error:", err);
      return null;
    }
  },

  // ── Section / Field operations ─────────────────────────────────────────────
  // There are no granular section/field endpoints in the old API.
  // All changes (add/edit/delete section or field) go through updateForm,
  // which sends the complete rebuilt sections array.
  //
  // These stubs are kept so FormBuilderPage doesn't need to change.
  // FormManagement.jsx calls handleSave() (→ updateForm) after every
  // section/field change anyway.

  addSection: async (formId, _payload) => ({ formId }),
  updateSection: async (formId, sectionId, _payload) => ({ formId, sectionId }),
  deleteSection: async (formId, sectionId) => ({ formId, sectionId }),
  reorderSections: async (formId, _sectionIds) => ({ formId }),

  addField: async (formId, sectionId, _payload) => ({ formId, sectionId }),
  updateField: async (formId, sectionId, fieldId, _payload) => ({ formId, sectionId, fieldId }),
  deleteField: async (formId, sectionId, fieldId) => ({ formId, sectionId, fieldId }),
  reorderFields: async (formId, sectionId, _fieldIds) => ({ formId, sectionId }),
};